import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  api,
  countButtons,
  markupFromPage,
  pageFromOwner,
  type BotCommand,
  type TreePage,
} from "@/lib/api";
import { useCan } from "@/lib/shell-context";
import { revealSoon } from "@/lib/utils";
import { TgHtml } from "@/components/rich-text";
import { useLanguages } from "@/lib/i18n";
import { languageName } from "@kickwin/shared";
import { MessageEditor, treeProblem } from "@/components/message-editor";
import { useConfirm } from "@/components/confirm";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Mono,
  Panel,
  Table,
  Td,
  Th,
  Toggle,
} from "@/components/primitives";

/**
 * Commands (spec §10, ticket 27).
 *
 * Every save pushes the list to Telegram's command menu for private chats, so
 * what players see offered is always what the bot answers. Commands never
 * create a Topic and are never copied to CS. The group commands (/revoke,
 * /blacklist, /unblacklist) are fixed and registered for group admins on
 * their own, so they are not edited here.
 */

interface Draft {
  id: number | null;
  command: string;
  description: string;
  /** The answer: text, optional image or video, and its button tree. */
  page: TreePage;
  isActive: boolean;
}

const EMPTY: Draft = {
  id: null,
  command: "",
  description: "",
  page: { text: "", buttons: [] },
  isActive: true,
};

function problemWith(d: Draft): string | null {
  if (!/^[a-z0-9_]{1,32}$/.test(d.command))
    return "The command can use lowercase letters, numbers and _ only (up to 32).";
  if (!d.description.trim()) return "Add a description; Telegram shows it in the command menu.";
  return treeProblem(d.page);
}

export function CommandsPage() {
  const { botId } = useParams();
  const canEdit = useCan("commands", "manage");
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [touched, setTouched] = useState(false);

  const commands = useQuery({
    queryKey: ["commands", botId],
    queryFn: () => api.get<BotCommand[]>(`/bots/${botId}/commands`),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["commands", botId] });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        command: d.command,
        description: d.description.trim(),
        responseText: d.page.text.trim(),
        replyMarkup: markupFromPage(d.page),
        // Saved menus are retired; the answer's own buttons replace them.
        menuId: null,
        isActive: d.isActive,
      };
      return d.id === null
        ? api.post(`/bots/${botId}/commands`, body)
        : api.patch(`/commands/${d.id}`, body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      setTouched(false);
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: number; isActive: boolean }) =>
      api.patch(`/commands/${id}`, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/commands/${id}`),
    onSuccess: invalidate,
  });

  const problem = draft ? problemWith(draft) : null;
  const languages = useLanguages(botId);

  return (
    <div className="grid grid-cols-1 gap-4">
      {draft && (
        <Panel
          id="command-form"
          title={draft.id === null ? "New command" : `Edit /${draft.command}`}
        >
          <form
            className="grid gap-3 px-5 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              setTouched(true);
              if (!problem) save.mutate(draft);
            }}
          >
            <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
              <Field label="Command">
                <div className="flex items-center rounded-md border border-input bg-card focus-within:border-ring">
                  <span className="mono pl-2.5 text-muted-foreground">/</span>
                  <Input
                    className="mono border-0 pl-0.5"
                    value={draft.command}
                    onChange={(e) =>
                      setDraft({ ...draft, command: e.target.value.toLowerCase().trim() })
                    }
                    placeholder="help"
                    maxLength={32}
                    autoCapitalize="none"
                    autoCorrect="off"
                  />
                </div>
              </Field>
              <Field label="Description in Telegram's menu">
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Get help from our team"
                  maxLength={256}
                />
              </Field>
            </div>
            {languages && languages.extra.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {languages.extra.map((lang) => (
                  <Field key={lang} label={`Menu description · ${languageName(lang)}`}>
                    <Input
                      value={draft.page.i18n?.[lang]?.description ?? ""}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          page: {
                            ...draft.page,
                            i18n: {
                              ...draft.page.i18n,
                              [lang]: {
                                ...draft.page.i18n?.[lang],
                                description: e.target.value || undefined,
                              },
                            },
                          },
                        })
                      }
                      placeholder={draft.description || "Uses the default"}
                      maxLength={256}
                    />
                  </Field>
                ))}
              </div>
            )}
            <div className="grid gap-1.5">
              <div className="font-medium">Answer</div>
              <div className="rounded-md border border-border p-3">
                <MessageEditor
                  value={draft.page}
                  onChange={(page) => setDraft({ ...draft, page })}
                  textPlaceholder="What the bot sends back."
                />
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2">
                <Toggle
                  label="Command is on"
                  checked={draft.isActive}
                  onChange={(isActive) => setDraft({ ...draft, isActive })}
                />
                {draft.isActive ? "On — offered in Telegram" : "Off — hidden and not answered"}
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving" : draft.id === null ? "Add command" : "Save changes"}
              </Button>
              <Button
                type="button"
                variant="quiet"
                onClick={() => {
                  setDraft(null);
                  setTouched(false);
                }}
              >
                Cancel
              </Button>
              {touched && problem && <span className="text-destructive">{problem}</span>}
            </div>
            {save.error && <ErrorNote error={save.error} />}
          </form>
        </Panel>
      )}

      <Panel
        title="Commands"
        action={
          canEdit &&
          !draft && (
            <Button size="sm" onClick={() => setDraft(EMPTY)}>
              <Plus className="size-4" /> New command
            </Button>
          )
        }
      >
        {commands.isLoading ? (
          <Loading />
        ) : commands.error ? (
          <div className="p-3.5">
            <ErrorNote error={commands.error} />
          </div>
        ) : commands.data && commands.data.length === 0 ? (
          <Empty
            title="No commands yet"
            hint="Players see these in the bot's menu button. /start, /help and /menu are good first ones."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th className="w-36">Command</Th>
                <Th>Description</Th>
                <Th className="md:max-xl:hidden">Reply</Th>
                <Th className="w-36">Buttons</Th>
                <Th className="w-16">On</Th>
                <Th className="w-36" />
              </tr>
            </thead>
            <tbody>
              {commands.data?.map((command) => (
                <tr key={command.id} className={command.isActive ? "" : "opacity-60"}>
                  <Td primary>
                    <Mono className="text-[13.5px] font-medium">/{command.command}</Mono>
                  </Td>
                  <Td label="Description">{command.description}</Td>
                  <Td label="Reply" className="text-muted-foreground md:max-xl:hidden">
                    {command.responseText ? (
                      <TgHtml
                        className="line-clamp-2"
                        html={pageFromOwner(command.responseText, command.replyMarkup).text}
                      />
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td label="Buttons" className="text-muted-foreground">
                    {(() => {
                      const page = pageFromOwner(command.responseText, command.replyMarkup);
                      const n = countButtons(page);
                      const parts = [
                        page.media ? (page.media.kind === "video" ? "video" : "image") : null,
                        n > 0 ? `${n} ${n === 1 ? "button" : "buttons"}` : null,
                      ].filter(Boolean);
                      return parts.length ? parts.join(" · ") : "—";
                    })()}
                  </Td>
                  <Td label="On">
                    <Toggle
                      label={`/${command.command} is ${command.isActive ? "on" : "off"}`}
                      checked={command.isActive}
                      disabled={!canEdit || patch.isPending}
                      onChange={(isActive) => patch.mutate({ id: command.id, isActive })}
                    />
                  </Td>
                  <Td>
                    {canEdit && (
                      <span className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            revealSoon("command-form");
                            setDraft({
                              id: command.id,
                              command: command.command,
                              description: command.description,
                              page: pageFromOwner(command.responseText, command.replyMarkup),
                              isActive: command.isActive,
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={remove.isPending}
                          onClick={async () => {
                            if (
                              await confirm({
                                title: `Delete /${command.command}?`,
                                confirmLabel: "Delete command",
                                destructive: true,
                              })
                            )
                              remove.mutate(command.id);
                          }}
                        >
                          Delete
                        </Button>
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {(patch.error || remove.error) && (
          <div className="p-3.5">
            <ErrorNote error={patch.error ?? remove.error} />
          </div>
        )}
        <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
          Saving updates Telegram's command menu straight away. /revoke, /blacklist and /unblacklist
          are set up for CS group admins automatically.
        </p>
      </Panel>
    </div>
  );
}
